import { type Client } from "graphql-ws";
import { Observable, type SubscribeFunction } from "relay-runtime";

import { fetchConfig } from "app/contexts";
import { getAuthToken } from "app/utils/auth";

/**
 * GraphQL subscriptions transport (graphql-transport-ws over a WebSocket).
 *
 * The platform serves subscriptions from a separate ASGI service, advertised
 * as `api.ws_uri` in application.json. Every deployment serves it. A missing
 * ws_uri (a local stack without the subscriptions service, or a platform
 * older than it) is handled quietly: the observable completes, and a
 * subscribing view simply hears nothing.
 */

let client: Client | null = null;
let clientPromise: Promise<Client | null> | null = null;

async function resolveWsUri(): Promise<string | null> {
  const config = await fetchConfig();
  return config.api?.ws_uri ?? null;
}

/**
 * Build the shared client on first use. Resolved once and memoised — the
 * client reconnects on its own, so there is never a reason to build a second.
 */
async function ensureClient(): Promise<Client | null> {
  if (clientPromise) {
    return clientPromise;
  }
  clientPromise = (async () => {
    // A throw in here used to be invisible twice over: both callers discard
    // the promise with `void`, and the rejected promise stayed memoised, so
    // one failure disabled subscriptions for the rest of the session with
    // nothing logged anywhere. Report it and let the next caller retry.
    const uri = await resolveWsUri();
    if (!uri) {
      return null;
    }
    // Imported here rather than at module scope so the transport isn't in
    // the module graph of everything that touches RelayEnvironment —
    // component tests never load it, and it stays out of the initial bundle
    // chunk.
    const { createClient } = await import("graphql-ws");
    client = createClient({
      url: uri,
      // The service authenticates once at connection_init and builds the
      // principal there, rather than per message. The credential travels in
      // the payload rather than a handshake header because a browser
      // WebSocket can't set one — which is what connection_init is for.
      //
      // The "Bearer " prefix is explicit here: over HTTP, relay's
      // authMiddleware adds it, but nothing does so on this path, and the
      // server's bare-token fallback is a deprecated branch that logs a
      // warning on every connection.
      connectionParams: async () => ({
        authorization: `Bearer ${await getAuthToken()}`,
      }),
      // The platform ALB's idle timeout is 60s and is shared with the main
      // API's listener, so it deliberately wasn't raised for subscriptions —
      // an idle socket stays alive only because the client pings. Ariadne
      // answers pings automatically.
      keepAlive: 20_000,
      retryAttempts: Infinity,
      shouldRetry: () => true,
    });
    return client;
  })().catch((error: unknown) => {
    console.error("could not start the subscriptions transport", error);
    clientPromise = null;
    return null;
  });
  return clientPromise;
}

/**
 * Resolve the config and load the transport chunk at startup, so the first
 * view to subscribe doesn't wait on a config fetch and a dynamic import.
 *
 * This does NOT open the socket: the client is lazy, and connects when
 * something first subscribes.
 */
export function primeSubscriptionClient(): void {
  void ensureClient();
}

export const subscribeFn: SubscribeFunction = (operation, variables) =>
  Observable.create((sink) => {
    let disposed = false;
    let unsubscribe: (() => void) | null = null;

    void ensureClient().then((resolved) => {
      if (disposed) {
        return;
      }
      if (!resolved) {
        // No subscriptions service to reach. Complete rather than error: a view
        // that hears nothing still renders what it fetched, where an error
        // here would surface as a broken view.
        sink.complete();
        return;
      }
      unsubscribe = resolved.subscribe(
        {
          operationName: operation.name,
          query: operation.text ?? "",
          variables,
        },
        {
          next: (value) => sink.next(value as never),
          error: (err) => sink.error(err as Error),
          complete: () => sink.complete(),
        },
      );
    });

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  });
